import { useState, useEffect, useRef } from "react";

/**
 * computes an SVG circle's
 * circumference from `radius`, then animates stroke-dashoffset from "0%
 * drawn" to the target percent over `duration` ms. Returns the current
 * dash values plus a live rounded percent (for a label counting up in
 * sync), matching the original's paired ring+text animation exactly.
 * Pass a changing `playKey` to replay (e.g. the AI Insights gauge, which
 * replays every time that tab is opened, not just once on load).
 */
export default function useRadialGauge(
  radius,
  targetPercent,
  duration,
  playKey,
) {
  const circumference = 2 * Math.PI * radius;
  const [offset, setOffset] = useState(circumference);
  const [displayPercent, setDisplayPercent] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    setOffset(circumference);
    setDisplayPercent(0);

    // Double rAF, matching the original: let the browser paint the 0%
    // state first so the transition to the target actually animates.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        setOffset(circumference - (targetPercent / 100) * circumference);
      });
      frameRef.current = raf2;
    });

    let start = null;
    function tick(ts) {
      if (start === null) start = ts;
      const elapsed = Math.min((ts - start) / duration, 1);
      setDisplayPercent(Math.round(elapsed * targetPercent));
      if (elapsed < 1) frameRef.current = requestAnimationFrame(tick);
    }
    frameRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf1);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, targetPercent, duration, playKey]);

  return { circumference, offset, displayPercent };
}
